import {formatLocalDate} from "./utils";

export async function handleInputChange(value, field, setFormData, type) {
    setFormData((prevData) => ({
        ...prevData,
        [field]:
            type === 'DATE' ? formatLocalDate(value) : value
    }));
}

/**
 * Universal date change handler for all date picker components
 * @param {Date|null} date - The selected date from the date picker
 * @param {string} field - The field name to update in the object
 * @param {Function} setFormData - The state setter function
 * @param {Object} options - Additional options for different use cases
 * @param {number} options.index - Index for array items (for list items)
 * @param {string} options.arrayField - Field name for array updates
 * @param {Function} options.setArrayData - State setter for array data
 * @param {Function} options.onDateChange - Custom callback after date change
 * @param {boolean} options.formatDate - Whether to format the date (default: true)
 * @param {boolean} options.allowNull - Whether to allow null dates (default: true)
 */
export function handleDateChange(date, field, setFormData, options = {}) {
    const {
        index,
        arrayField,
        setArrayData,
        onDateChange,
        formatDate = true,
        allowNull = true
    } = options;

    // Handle null date
    if (!date && !allowNull) {
        return;
    }

    // Format the date if requested
    const processedDate = date && formatDate ? formatLocalDate(date) : date;

    // If we're updating an array item
    if (index !== undefined && arrayField && setArrayData) {
        setArrayData((prevData) =>
            prevData.map((item, i) =>
                i === index ? {
                    ...item,
                    [field]: processedDate
                } : item
            )
        );
    } else {
        // Standard form data update
        setFormData((prevData) => ({
            ...prevData,
            [field]: processedDate
        }));
    }

    // Call custom callback if provided
    if (onDateChange) {
        onDateChange(date, field, processedDate);
    }
}

/**
 * Simplified date change handler for basic form fields
 * @param {Date|null} date - The selected date
 * @param {string} field - The field name
 * @param {Function} setFormData - The state setter function
 */
export function handleSimpleDateChange(date, field, setFormData) {
    handleDateChange(date, field, setFormData);
}

/**
 * Date change handler for array items (like rental items, prescription medications, etc.)
 * @param {number} index - The index of the item in the array
 * @param {Date|null} date - The selected date
 * @param {string} field - The field name
 * @param {Function} setArrayData - The state setter function for the array
 */
export function handleArrayItemDateChange(index, date, field, setArrayData) {
    handleDateChange(date, field, null, {
        index,
        setArrayData
    });
}

/**
 * Date change handler with custom formatting options
 * @param {Date|null} date - The selected date
 * @param {string} field - The field name
 * @param {Function} setFormData - The state setter function
 * @param {Object} options - Formatting options
 * @param {boolean} options.formatDate - Whether to format the date
 * @param {Function} options.customFormatter - Custom date formatter function
 */
export function handleFormattedDateChange(date, field, setFormData, options = {}) {
    const { formatDate = true, customFormatter } = options;
    
    let processedDate = date;
    
    if (date && formatDate) {
        processedDate = customFormatter ? customFormatter(date) : formatLocalDate(date);
    }
    
    handleDateChange(date, field, setFormData, {
        formatDate: false,
        onDateChange: () => {
            setFormData((prevData) => ({
                ...prevData,
                [field]: processedDate
            }));
        }
    });
}